import { Stripe, StripeCardElement } from "@stripe/stripe-js";
import { axiosService } from "network/axios";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  gettingProjectRequest,
  updateProjectRequest,
} from "store/customerProject/reducer";
import { setAlertMessageRequest } from "store/ui/reducer";
import type { CardInfo, Order } from "../types";

export function useServicePayment(params: {
  item: { id: number; key: string; perPageUsd?: number; price: number };
  selectedOrder: Order | null;
  cardInfo: CardInfo | null;
  isShippingValid: boolean;
  shippingRecipient: string;
  shippingStreet: string;
  shippingCity: string;
  shippingZip: string;
  shippingState: string;
  shippingCountry: string;
  saveCardAsDefault: boolean;
  userId: string | number;
  onClose: () => void;
  onRefresh?: Function;
  setIsProcessingPayment: (v: boolean) => void;
}) {
  const {
    item,
    selectedOrder,
    cardInfo,
    isShippingValid,
    shippingRecipient,
    shippingStreet,
    shippingCity,
    shippingZip,
    shippingState,
    shippingCountry,
    saveCardAsDefault,
    userId,
    onClose,
    onRefresh,
    setIsProcessingPayment,
  } = params;

  const dispatch = useDispatch();

  const proceedPayment = useCallback(
    async (serviceId: number, payload: any) => {
      try {
        const orderId = selectedOrder?.order_id;
        if (!orderId) return;

        await axiosService.post(
          `/shipping/process-service-payment/${orderId}/${serviceId}`,
          payload
        );

        dispatch(
          setAlertMessageRequest({
            type: "success",
            message: "Service was added to selected Payment.",
          })
        );
        dispatch(gettingProjectRequest({ user_id: Number(userId) }));
      } catch (err: any) {
        dispatch(
          setAlertMessageRequest({
            type: "error",
            message: err?.data?.messages?.error || "Error in Processing Payment.",
          })
        );
      } finally {
        setIsProcessingPayment(false);
      }

      onRefresh?.();
      onClose();
    },
    [dispatch, onClose, onRefresh, selectedOrder?.order_id, setIsProcessingPayment, userId]
  );

  const onPayNow = useCallback(async () => {
    setIsProcessingPayment(true);
    const stripe: Stripe | null | undefined = cardInfo?.stripe;
    const cardElement: StripeCardElement | null | undefined =
      cardInfo?.cardElement;
    const cardHolderName = cardInfo?.cardHolderName ?? "";
    const cardZipCode = cardInfo?.cardZipCode ?? "";

    if (saveCardAsDefault === true) {
      if (stripe) {
        stripe
          .createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: (cardInfo as any)?.cardHolderName,
            },
          })
          .then(async function (result) {
            const paymentMethodId = result?.paymentMethod?.id;
            await axiosService.post(`/shipping/put/payment-method/${userId}`,
              {
                payment_method_id: paymentMethodId,
                is_default: 1,
              }
            );
          });
      }
    }

    const serviceId = item.id;

    const isMailHardCopyService = item?.key === "include_mail_hard_copy";

    if (isMailHardCopyService && !isShippingValid) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message: "Please fill in all shipping details.",
        })
      );
      setIsProcessingPayment(false);
      return;
    }

    const addressFields = isMailHardCopyService
      ? [
          shippingRecipient.trim(),
          shippingStreet.trim(),
          shippingCity.trim(),
          shippingZip.trim(),
          shippingState.trim(),
          shippingCountry.trim(),
        ].join("; ")
      : null;

    const pageCount = Math.max(0, Number(selectedOrder?.page_count || 0) || 0);
    const effectiveAmount = item.perPageUsd
      ? item.perPageUsd * pageCount
      : item.price;

    let paymentPayload = null;
    if ((cardInfo as any)?.customer_id) {
      paymentPayload = {
        amount: effectiveAmount,
        customer_id: (cardInfo as any)?.customer_id,
        payment_method_id: (cardInfo as any)?.pm_id,
      };

      await proceedPayment(serviceId, paymentPayload);

      if (isMailHardCopyService && selectedOrder?.order_id && addressFields) {
        dispatch(
          updateProjectRequest({
            user_id: userId,
            order_id: Number(selectedOrder.order_id),
            details: { address_fields: addressFields },
          })
        );
      }
    } else {
      stripe
        ?.createToken(cardElement as any, {
          name: cardHolderName,
          address_zip: cardZipCode,
        })
        .then(async (res) => {
          const tokenId = res?.token?.id;
          if (tokenId) {
            paymentPayload = {
              amount: effectiveAmount,
              tokenId: tokenId,
            };
            await proceedPayment(serviceId, paymentPayload);

            if (
              isMailHardCopyService &&
              selectedOrder?.order_id &&
              addressFields
            ) {
              dispatch(
                updateProjectRequest({
                  user_id: userId,
                  order_id: Number(selectedOrder.order_id),
                  details: { address_fields: addressFields },
                })
              );
            }
          } else if (res?.error?.message) {
            dispatch(
              setAlertMessageRequest({
                type: "error",
                message:
                  res?.error?.message ||
                  "Error in processing the payment. Please contact to support team.",
              })
            );
          } else {
            dispatch(
              setAlertMessageRequest({
                type: "error",
                message:
                  "Error in generating token Id. please check the card information.",
              })
            );
          }
        })
        .catch(async (err) => {
          dispatch(
            setAlertMessageRequest({
              type: "error",
              message:
                err?.error?.message ||
                "Error in processing the payment. Please contact to support team.",
            })
          );
          setIsProcessingPayment(false);
        });
    }
  }, [
    cardInfo,
    dispatch,
    isShippingValid,
    item.id,
    item.key,
    item.perPageUsd,
    item.price,
    proceedPayment,
    saveCardAsDefault,
    selectedOrder?.order_id,
    selectedOrder?.page_count,
    setIsProcessingPayment,
    shippingCity,
    shippingCountry,
    shippingRecipient,
    shippingState,
    shippingStreet,
    shippingZip,
    userId,
  ]);

  return { proceedPayment, onPayNow };
}
